<template>
  <div>
    <b-row v-if="details.type === 'DIR'"
           class="pb-3"
           no-gutters
    >
      <b-col>
        <div class="d-flex justify-content-center">
          <img :src="iconImage"
               alt=""
               class="cloud-details-img"
               loading="lazy"
          />
        </div>
      </b-col>
    </b-row>

    <div v-else>
      <b-row no-gutters>
        <b-col>
          <div class="text-muted small text-left mb-3">
            {{ $t('cloud.details_view.preview') }}
          </div>
        </b-col>
      </b-row>

      <b-row class="mb-3" no-gutters>
        <b-col>
          <div class="image-wrapper">
            <sdui-loader v-if="$wait.waiting('fetch-details')" class="p-4 m-4 left-spinner" />
            <SLightbox
              v-else-if="file.meta && file.meta.has_thumbnail"
              :file-items="[file]"
              :re-fetch-fn="reFetchFileDetails"
              img-container-class="chat-img"
              is-within-modal
            />
            <div v-else class="d-flex align-items-center">
              <img class="cloud-details-img"
                   :src="iconImage"
                   alt=""
                   loading="lazy"
              />
            </div>
          </div>
        </b-col>
      </b-row>
    </div>

    <b-row v-if="details.type === 'DIR'" no-gutters>
      <b-col>
        <SList>
          <SListItem
            v-if="details.uploadLimitedAt"
            :title="$t('cloud.cloud_list.modals.details.list.upload_limited_at.title')"
            :right-action-label="$dateFormat.date_time(details.uploadLimitedAt)"
          />
          <SListItem :title="$t('cloud.cloud_list.modals.details.list.name.title')" :right-action-label="file.name" />
          <SListItem
            :title="$t('cloud.cloud_list.modals.details.list.last_changed_at.title')"
            :right-action-label="$dateFormat.date_time(file.updated_at)"
          />
          <SListItem
            :title="$t('cloud.cloud_list.modals.details.list.type.title')"
            :right-action-label="$t('cloud.cloud_list.modals.details.list.type.label')"
          />
          <SListItem
            :title="$t('cloud.cloud_list.modals.details.list.path.title')"
            :right-action-label="file.meta ? file.meta.absolute_path : ''"
          />
        </SList>
      </b-col>
    </b-row>

    <b-row v-else no-gutters>
      <b-col>
        <SList v-if="file">
          <SListItem :title="$t('cloud.cloud_list.modals.details.list.name.title')" :right-action-label="file.name" />
          <SListItem
            :title="$t('cloud.cloud_list.modals.details.list.last_changed_at.title')"
            :right-action-label="$dateFormat.date_time(file.updated_at)"
          />
          <SListItem
            class="text-uppercase-label"
            :title="$t('cloud.cloud_list.modals.details.list.type.title')"
            :right-action-label="file.extension"
          />
          <SListItem :title="$t('cloud.cloud_list.modals.details.list.size.title')" :right-action-label="$getSize(file.size, false)" />
          <SListItem
            :title="$t('cloud.cloud_list.modals.details.list.path.title')"
            :right-action-label="file.meta ? file.meta.absolute_path : ''"
          />
          <SListItem
            v-if="isImage"
            data-test="file-details-alt-text"
            :title="$t('cloud.cloud_list.modals.details.list.alt_text.title')"
            :right-action-label="file.alt_name"
          />
        </SList>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import SduiLoader from 'src/components/miscellaneous/ui/common/SduiLoader.vue'
import fileMixin from 'src/mixins/channels/fileMixin.js'
import { waitFor } from 'src/config/setup/plugins/waitFor/index.js'
import {defineAsyncComponent} from 'vue'

const SLightbox = defineAsyncComponent(() => import('@/components/base/SLightbox/SLightbox.vue'))

export default {
  name: 'CloudFileDetails',
  components: {SLightbox, SduiLoader },
  mixins: [fileMixin],
  props: {
    details: {
      type: Object,
      default: () => ({}),
    },
  },

  data: () => ({
    file: {},
  }),

  computed: {
    isFolder() {
      return this.details.type === 'DIR'
    },

    iconImage() {
      return this.getIconImage(this.iconName)
    },

    iconName() {
      if (this.isFolder && this.details.isProtected) return 'folder-upload'
      if (this.isFolder) return 'folder'
      return this.getFileIconName(this.file)
    },

    isImage() {
      if (!this.file) return false
      return this.file.can_show_preview || this.file.type?.startsWith('image')
    },
  },

  methods: {
    fetchFileDetails: waitFor('fetch-details', async function () {
      const res = await this.$axios.get(this.details.api)
      this.file = res.data
    }),

    async reFetchFileDetails() {
      const res = await this.$axios.get(this.details.api)
      return res.data
    }
  },

  async mounted() {
    await this.fetchFileDetails()
  },
}
</script>

<style scoped lang="scss">
.custom-img-radius {
  border-radius: 7px;
}

.icon-wrapper {
  position: relative;
}

.image-wrapper {
  display: flex;
  justify-content: center;

  :deep(.chat-img) {
    border-radius: 13px;
    max-width: 100%;
    max-height: 200px;
    height: auto;
    width: auto;
  }

  .left-spinner::before {
    left: 16px;
  }
}

:deep(.sdui-list-item__right-action-wrapper) {
  max-width: calc(100% - 80px);
}

:deep(.text-uppercase-label .sdui-list-item__right-action-wrapper) {
  text-transform: uppercase;
}

.cloud-details-img {
  min-width: 64px;
}
</style>
